<script>
  import { sgettext } from '../../../../i18n.js';
  import { submit } from '../../../../utils.js';

  export let activityVersionId;
  export let item = null;
  export let fields;
  export let editable = true;
  export let retrieveItems;
  export let actionUrl;
  let isWaiting = false;

  let validateItemCount = itemCount => {
    if (typeof itemCount !== 'number' || itemCount < 0) return null;
    else if (!Number.isInteger(itemCount)) return Math.floor(itemCount);
    else return itemCount;
  };

  // Creating
  export let isCreating = false;
  let createValues = {name: '', description: '', count: null};
  let cancelCreatingMode = () => {
    isWaiting = false;
    isCreating = false;
  };
  $: isValidCreateForm = createValues.name ? true : false;
  async function addItem() {
    if (!isValidCreateForm) return;

    let payload = {
      item_name: createValues.name,
      item_description: createValues.description,
      item_count: validateItemCount(createValues.count),
    };

    isWaiting = true;
    const response = await submit(actionUrl, 'POST', payload);

    if (response.ok) {
      await retrieveItems();
      cancelCreatingMode();
    }
  }

  // Editing
  let isEditing = false;
  let toEditItem = null;
  let editValues = {name: '', description: '', count: null};
  let setEditingMode = item => {
    if (editable) {
      isEditing = true;
      toEditItem = {
        id: item.id,
        name: item.item_name,
        description: item.item_description,
        count: item.item_count,
      };
      editValues = {...toEditItem};
    }
  };
  let cancelEditingMode = () => {
    isWaiting = false;
    isEditing = false;
    toEditItem = null;
    editValues = {name: '', description: '', count: null};
  };
  $: isValidEditForm = editValues.name ? true : false;
  async function updateItem(item) {
    if (!isValidEditForm) return;

    let payload = {
      item_name: editValues.name,
      item_description: editValues.description,
      item_count: validateItemCount(editValues.count),
    };

    isWaiting = true;
    const url = `${actionUrl}${item.id}/`;
    const response = await submit(url, 'PUT', payload);

    if (response.ok) {
      await retrieveItems();
      cancelEditingMode();
    }
  }

  // Deleting
  export let isDeleting = false;
  export let toDeleteItem = null;
  let tryToDelete = (item=null) => {
    if (item) {
      isDeleting = true;
      toDeleteItem = {
        id: item.id,
        name: item.item_name,
        description: item.item_description,
        count: item.item_count,
      };
    }
  };
</script>

{#if isCreating}
  <tr>
    <td>
      <!-- svelte-ignore a11y-autofocus -->
      <input
        type="text"
        class="form-control"
        placeholder={`${fields[0]}*`}
        bind:value={createValues.name}
        autofocus={true}
        readonly={isWaiting}
      />
    </td>
    <td>
      <input
        type="text"
        class="form-control"
        placeholder={fields[1]}
        bind:value={createValues.description}
        readonly={isWaiting}
      />
    </td>
    <td>
      <input
        type="number"
        min="0"
        step="1"
        class="form-control"
        placeholder={fields[2]}
        bind:value={createValues.count}
        readonly={isWaiting}
      />
    </td>
    <td class="actions">
      <button
        class="btn btn-sm btn-primary mr-2"
        on:click={addItem}
        disabled={!isValidCreateForm || isWaiting}
      >
        {#if isWaiting}
          {sgettext('$$ADDING_$$')}
        {:else}
          {sgettext('$$ADD_1$$')}
        {/if}
      </button>
      {#if isWaiting}
        <div class="d-inline-block mx-2">
          <div
            class="spinner-border spinner-border-sm text-primary"
            role="status"
          >
            <span class="sr-only">{sgettext('$$LOADING$$')}...</span>
          </div>
        </div>
      {:else}
        <button class="btn btn-sm btn-outline" on:click={cancelCreatingMode}>
          {sgettext('$$CANCEL$$')}
        </button>
      {/if}
    </td>
  </tr>
{:else if isEditing && item?.id && toEditItem?.id && item.id === toEditItem.id}
  <tr>
    <td>
      <!-- svelte-ignore a11y-autofocus -->
      <input
        type="text"
        class="form-control"
        placeholder={`${fields[0]}*`}
        bind:value={editValues.name}
        autofocus={true}
        readonly={isWaiting}
      />
    </td>
    <td>
      <input
        type="text"
        class="form-control"
        placeholder={fields[1]}
        bind:value={editValues.description}
        readonly={isWaiting}
      />
    </td>
    <td>
      <input
        type="number"
        min="0"
        step="1"
        class="form-control"
        placeholder={fields[2]}
        bind:value={editValues.count}
        readonly={isWaiting}
      />
    </td>
    <td class="actions">
      <button
        class="btn btn-sm btn-primary mr-2"
        on:click={() => updateItem(item)}
        disabled={!isValidEditForm || isWaiting}
      >
        {#if isWaiting}
          {sgettext('$$UPDATING_$$')}
        {:else}
          {sgettext('$$UPDATE_1$$')}
        {/if}
      </button>
      {#if isWaiting}
        <div class="d-inline-block mx-2">
          <div
            class="spinner-border spinner-border-sm text-primary"
            role="status"
          >
            <span class="sr-only">{sgettext('$$LOADING$$')}...</span>
          </div>
        </div>
      {:else}
        <button class="btn btn-sm btn-outline" on:click={cancelEditingMode}>
          {sgettext('$$CANCEL$$')}
        </button>
      {/if}
    </td>
  </tr>
{:else if item?.id}
  <tr>
    <td>{item.item_name}</td>
    <td>{item.item_description}</td>
    <td
      >{#if item.item_count !== null}{item.item_count}{/if}</td
    >
    {#if editable}
      <td class="actions">
        {#if !isEditing && !isCreating}
          <span on:click={() => setEditingMode(item)}>
            <i class="fas fa5-edit" />
          </span>
          <span on:click={() => tryToDelete(item)}>
            <i class="fas fa5-trash-alt" />
          </span>
        {/if}
      </td>
    {/if}
  </tr>
{/if}

<style>
  .actions {
    text-align: right;
    min-width: 130px;
    white-space: nowrap;
  }

  .actions i {
    margin: 0 0 0 20px;
    cursor: pointer;
  }
</style>
